import type { PropsWithChildren } from "react";
import { createContext, useCallback, useContext, useRef, useState, useSyncExternalStore } from "react";
import type { AttemptReasonView } from "@remi/contracts/services/task.service";
import { remiLogger } from "@remi/logging";
import { RemiButton, useEffect } from "@remi/next-ui";
import type { CacheMessageForType } from "@remi/types/socket/message";
import { CacheMessageType } from "@remi/types/socket/message";

import { api } from "~/utils/api";
import { displayToast } from "~/components/ui/sonner";
import type { AttemptTrackerPhase } from "~/contexts/attemptTrackerRules";
import {
  ABORT_DISPOSITION_BY_REASON,
  classifyProjectTasksMessage,
  GENERIC_ATTEMPT_ERROR,
  PHASE_RANK,
  streakGateCopy,
} from "~/contexts/attemptTrackerRules";
import { DetachableChannelProvider } from "~/contexts/DetachableChannelProvider";
import { useCacheChannel } from "~/contexts/hooks/useCacheChannel";
import { useUserProfile } from "~/contexts/UserProfileContext";
import { getCacheChannelName } from "~/contexts/utils/cache";
import type { RecordAttemptSubmission } from "~/hooks/tasks/useRecordAttempt";

const ATTEMPT_SETTLE_TIMEOUT_MS = 15_000;

// Sized against the workflow's own budget: `timeouts.finish` (60m) in
// packages/inngest/src/functions/task-attempts/attempt-requested.ts bounds the retry ceiling, and this
// sits far below it so a stranded card retires while the workflow may still be retrying.
const ATTEMPT_ENTRY_TTL_MS = 300_000;

const SETTLED_ENTRY_GRACE_MS = 60_000;

// Long enough that the live-region announcement is not torn down mid-read.
const ATTEMPT_TOAST_DURATION_MS = 10_000;

// Deliberately non-committal: a lost committed publish means this can fire for an attempt that DID
// durably record, so the copy must not assert failure.
const UNCONFIRMED_ATTEMPT = "We couldn't confirm that attempt. Refreshing this task.";

export interface AttemptTrackerEntry {
  dedupeKey: string;
  taskId: string;
  projectId: number;
  personId: string;
  submission: RecordAttemptSubmission;
  attemptNumber: number | null;
  followUpDate: Date;
  submittedAt: number;
  phase: AttemptTrackerPhase;
  // False until the mutation's onSuccess — entries exist pre-ack because the channel attach must start
  // at onMutate, a round-trip before the server accepts the attempt.
  acknowledged: boolean;
  // Carried on the entry rather than re-fetched: the pending card must render in the same commit as the
  // submit, and a query here would turn an instant card into no card for as long as it was in flight.
  projection: AttemptReasonView | null;
  onViewProject?: () => void;
}

interface RegisterAttemptInput {
  dedupeKey: string;
  taskId: string;
  projectId: number;
  submission: RecordAttemptSubmission;
  attemptNumber: number | null;
  followUpDate: Date;
  projection: AttemptReasonView | null;
  /** Adds "View project" to the "Attempt recorded" toast, for surfaces the task may have just left. */
  onViewProject?: () => void;
}

interface AttemptTrackerContextValue {
  register: (input: RegisterAttemptInput) => void;
  confirmSubmitted: (dedupeKey: string) => void;
  discard: (dedupeKey: string) => void;
  registerFeed: (taskId: string) => () => void;
  ownsDedupeKey: (dedupeKey: string) => boolean;
  subscribe: (taskId: string, onStoreChange: () => void) => () => void;
  getEntriesForTask: (taskId: string) => readonly AttemptTrackerEntry[];
}

const EMPTY_ENTRIES: readonly AttemptTrackerEntry[] = Object.freeze([]);

const NO_TRACKER: AttemptTrackerContextValue = {
  register: () => undefined,
  confirmSubmitted: () => undefined,
  discard: () => undefined,
  registerFeed: () => () => undefined,
  ownsDedupeKey: () => false,
  subscribe: () => () => undefined,
  getEntriesForTask: () => EMPTY_ENTRIES,
};

const AttemptTrackerContext = createContext<AttemptTrackerContextValue>(NO_TRACKER);

export const usePendingAttemptForTask = (taskId: string): readonly AttemptTrackerEntry[] => {
  const tracker = useContext(AttemptTrackerContext);
  const subscribe = useCallback(
    (onStoreChange: () => void) => tracker.subscribe(taskId, onStoreChange),
    [tracker, taskId],
  );
  const getSnapshot = useCallback(() => tracker.getEntriesForTask(taskId), [tracker, taskId]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};

export const useAttemptTracker = (): AttemptTrackerContextValue => useContext(AttemptTrackerContext);

interface EntryTimers {
  settle: ReturnType<typeof setTimeout> | null;
  ttl: ReturnType<typeof setTimeout> | null;
  grace: ReturnType<typeof setTimeout> | null;
}

interface TrackerStore {
  entries: Map<string, AttemptTrackerEntry>;
  timers: Map<string, EntryTimers>;
  byTask: Map<string, readonly AttemptTrackerEntry[]>;
  listeners: Map<string, Set<() => void>>;
  feeds: Map<string, number>;
}

const createStore = (): TrackerStore => ({
  entries: new Map(),
  timers: new Map(),
  byTask: new Map(),
  listeners: new Map(),
  feeds: new Map(),
});

const ATTEMPT_CHANNEL_OPTIONS = { params: { rewind: "1" } };

export const AttemptTrackerProvider = ({ children }: PropsWithChildren) => {
  const { user } = useUserProfile();
  const utils = api.useUtils();
  // Read through refs, not the closure: the context value is built once so consumers never re-render,
  // which would otherwise pin both of these to the first render's values.
  const utilsRef = useRef(utils);
  utilsRef.current = utils;
  const personIdRef = useRef(user.person.id);
  personIdRef.current = user.person.id;

  const storeRef = useRef<TrackerStore | null>(null);
  storeRef.current ??= createStore();
  const store = storeRef.current;

  const [projectIds, setProjectIds] = useState<readonly number[]>([]);

  const contextRef = useRef<AttemptTrackerContextValue | null>(null);
  const dispatchRef = useRef<((message: CacheMessageForType<typeof CacheMessageType.PROJECT_TASKS>) => void) | null>(
    null,
  );
  const onChannelMessage = useRef((message: CacheMessageForType<typeof CacheMessageType.PROJECT_TASKS>) => {
    dispatchRef.current?.(message);
  }).current;

  if (contextRef.current === null) {
    const publish = (taskId: string) => {
      const next = Array.from(store.entries.values()).filter((entry) => entry.taskId === taskId);
      if (next.length === 0) {
        store.byTask.delete(taskId);
      } else {
        store.byTask.set(taskId, next);
      }
      for (const listener of store.listeners.get(taskId) ?? []) {
        listener();
      }
    };

    const syncProjectIds = () => {
      const live = new Set(Array.from(store.entries.values()).map((entry) => entry.projectId));
      setProjectIds((current) =>
        current.length === live.size && current.every((id) => live.has(id)) ? current : Array.from(live),
      );
    };

    const clearTimers = (dedupeKey: string) => {
      const timers = store.timers.get(dedupeKey);
      if (timers === undefined) {
        return;
      }
      for (const timer of [timers.settle, timers.ttl, timers.grace]) {
        if (timer !== null) {
          clearTimeout(timer);
        }
      }
      store.timers.delete(dedupeKey);
    };

    const removeEntry = (dedupeKey: string) => {
      const entry = store.entries.get(dedupeKey);
      if (entry === undefined) {
        return;
      }
      clearTimers(dedupeKey);
      store.entries.delete(dedupeKey);
      publish(entry.taskId);
      syncProjectIds();
    };

    const writeEntry = (entry: AttemptTrackerEntry) => {
      store.entries.set(entry.dedupeKey, entry);
      publish(entry.taskId);
    };

    const transition = (entry: AttemptTrackerEntry, next: AttemptTrackerPhase): boolean => {
      if (PHASE_RANK[next] <= PHASE_RANK[entry.phase]) {
        return false;
      }
      writeEntry({ ...entry, phase: next });
      return true;
    };

    const refetchCommitted = (entry: AttemptTrackerEntry) => {
      const { task, project, favorites, workOrder, workOrderConcession } = utilsRef.current;
      void task.getByProjectId.invalidate({ projectId: entry.projectId });
      void task.getTodaysActions.invalidate();
      void task.getContractorPlate.invalidate();
      void task.getTaskById.invalidate(entry.taskId);
      void task.getAttemptContext.invalidate({ taskId: entry.taskId });
      void project.byId.invalidate(entry.projectId);
      // Scoped, never blanket: a no-arg invalidate refetches every mounted activity feed.
      void task.getTaskActivityLog.invalidate({ taskId: entry.taskId });
      // Superset invariant: this must cover everything the cache manager's skip suppresses on the owning
      // tab. An attempt can create or resolve a holdup, and these two drive the blocker UI on that page.
      void project.getBlockers.invalidate();
      void project.getBlockings.invalidate();
      // Blanket by necessity, not oversight: a blocking card is keyed by the BLOCKED task's id, which
      // the attempting client never learns — it only knows the task it just attempted.
      void task.getBlockingCards.invalidate();
      void favorites.listMine.invalidate();
      // A concession request's review task commits inside the workflow, so the drawer's concession reads only
      // see it on a refetch that runs after settled.
      void workOrderConcession.byTaskId.invalidate({ taskId: entry.taskId });
      void workOrder.getActiveByProjectId.invalidate({ projectId: entry.projectId });
    };

    // Unconditional, even after a committed refetch: four workflow steps run between the two publishes
    // and mutate data the committed fetch could not have seen. getTaskById and getAttemptContext are
    // tracker-only, and the legacy task-details branch mounts no cache manager to cover them.
    const refetchSettled = (entry: AttemptTrackerEntry) => {
      refetchCommitted(entry);
      void utilsRef.current.project.getProjectsForMilestoneTable.invalidate();
    };

    // Derived from what is actually mounted, never from a claim the caller passes in: a prop passed at
    // call time cannot reflect whether the feed is mounted in an unrelated subtree.
    const announceRecorded = (previousPhase: AttemptTrackerPhase, entry: AttemptTrackerEntry) => {
      const feedIsRendering = (store.feeds.get(entry.taskId) ?? 0) > 0;
      if (PHASE_RANK[previousPhase] >= PHASE_RANK.committed || feedIsRendering) {
        return;
      }
      displayToast({
        title: "Attempt recorded",
        variant: "success",
        link:
          entry.onViewProject === undefined ? undefined : (
            <RemiButton
              variant="none"
              className="type-xs-strong self-start px-0 text-text-accent"
              onClick={entry.onViewProject}
            >
              View project
            </RemiButton>
          ),
      });
    };

    const handleCommitted = (entry: AttemptTrackerEntry) => {
      const previousPhase = entry.phase;
      if (!transition(entry, "committed")) {
        return;
      }
      const timers = store.timers.get(entry.dedupeKey);
      if (timers?.settle != null) {
        clearTimeout(timers.settle);
        timers.settle = null;
      }
      remiLogger.info("task.attempt.client.committed", {
        taskId: entry.taskId,
        dedupeKey: entry.dedupeKey,
        latencyMs: Date.now() - entry.submittedAt,
      });
      refetchCommitted(entry);
      announceRecorded(previousPhase, entry);
    };

    const handleSettled = (entry: AttemptTrackerEntry) => {
      const previousPhase = entry.phase;
      if (!transition(entry, "settled")) {
        return;
      }
      const timers = store.timers.get(entry.dedupeKey);
      if (timers?.settle != null) {
        clearTimeout(timers.settle);
        timers.settle = null;
      }
      refetchSettled(entry);
      announceRecorded(previousPhase, entry);
      // The entry outlives settlement on purpose: the note overlay and the holdup shimmer are keyed to
      // it and must survive the pending→real card swap. Rendering already hides it once the fetched
      // feed carries its dedupeKey.
      if (timers !== undefined) {
        if (timers.ttl !== null) {
          clearTimeout(timers.ttl);
          timers.ttl = null;
        }
        timers.grace = setTimeout(() => removeEntry(entry.dedupeKey), SETTLED_ENTRY_GRACE_MS);
      }
    };

    const handleFailed = (entry: AttemptTrackerEntry) => {
      // FailureEventArgs carries no step output, so the server cannot tell "never committed" from
      // "committed, then enrichment failed" — but the observed phase can. A failure after committed
      // means the attempt durably recorded, and claiming otherwise would be wrong.
      const observedCommitted = PHASE_RANK[entry.phase] >= PHASE_RANK.committed;
      remiLogger.warn("task.attempt.client.failed", {
        taskId: entry.taskId,
        dedupeKey: entry.dedupeKey,
        observedCommitted,
      });
      refetchCommitted(entry);
      if (!observedCommitted) {
        displayToast({ title: UNCONFIRMED_ATTEMPT, variant: "error", duration: ATTEMPT_TOAST_DURATION_MS });
      }
      removeEntry(entry.dedupeKey);
    };

    const handleAbort = (entry: AttemptTrackerEntry, abortReason: string) => {
      const disposition = ABORT_DISPOSITION_BY_REASON.get(abortReason) ?? {
        rollback: true,
        message: GENERIC_ATTEMPT_ERROR,
      };
      const copy = abortReason === "STREAK_GATE_UNMET" ? streakGateCopy(entry.projection?.gate) : disposition.message;
      remiLogger.warn("task.attempt.client.aborted", {
        taskId: entry.taskId,
        abortReason,
        dedupeKey: entry.dedupeKey,
      });
      // Invalidate rather than restore a snapshot: an abort can land after other refetches brought in
      // fresher data, and replaying a stale snapshot over it would clobber them.
      if (disposition.rollback) {
        refetchCommitted(entry);
      }
      if (copy !== null) {
        displayToast({ title: copy, variant: "error", duration: ATTEMPT_TOAST_DURATION_MS });
      }
      // A non-rollback abort means another arm on this dedupeKey is still going to commit, and handleMessage
      // returns early once the entry is gone — so removing it here loses the winner's later publishes. The
      // settle and TTL timers stay armed, so onTtl remains the backstop.
      if (disposition.rollback) {
        removeEntry(entry.dedupeKey);
      }
    };

    const onSettleTimeout = (dedupeKey: string) => {
      const entry = store.entries.get(dedupeKey);
      if (entry === undefined) {
        return;
      }
      const timers = store.timers.get(dedupeKey);
      if (timers !== undefined) {
        timers.settle = null;
      }
      // Regressive against an already-observed committed, so this both skips the transition and keeps
      // the timed-out log honest: it fires only when committed genuinely has not arrived.
      if (!transition(entry, "delayed")) {
        return;
      }
      remiLogger.warn("task.attempt.client.settle-timed-out", { taskId: entry.taskId, dedupeKey });
      refetchCommitted(entry);
    };

    const onTtl = (dedupeKey: string) => {
      const entry = store.entries.get(dedupeKey);
      if (entry === undefined) {
        return;
      }
      const observedCommitted = PHASE_RANK[entry.phase] >= PHASE_RANK.committed;
      refetchCommitted(entry);
      remiLogger.warn("task.attempt.client.expired", {
        taskId: entry.taskId,
        dedupeKey,
        lastObservedPhase: entry.phase,
      });
      // An attempt seen to commit is recorded, and a workflow whose enrichment retries past this TTL is
      // the designed path, not a failure to report.
      if (!observedCommitted) {
        displayToast({ title: UNCONFIRMED_ATTEMPT, variant: "error", duration: ATTEMPT_TOAST_DURATION_MS });
      }
      removeEntry(dedupeKey);
    };

    const handleMessage = (message: CacheMessageForType<typeof CacheMessageType.PROJECT_TASKS>) => {
      const dedupeKey = message.attemptDedupeKey;
      if (dedupeKey === undefined) {
        return;
      }
      const entry = store.entries.get(dedupeKey);
      if (entry === undefined) {
        return;
      }
      const classification = classifyProjectTasksMessage(message);
      switch (classification.kind) {
        case "ordinary":
          return;
        case "aborted":
          handleAbort(entry, classification.reason);
          return;
        case "committed":
          handleCommitted(entry);
          return;
        case "failed":
          handleFailed(entry);
          return;
        case "settled":
          handleSettled(entry);
          return;
      }
    };

    dispatchRef.current = handleMessage;

    contextRef.current = {
      register: (input) => {
        writeEntry({
          ...input,
          personId: personIdRef.current,
          submittedAt: Date.now(),
          phase: "pending",
          acknowledged: false,
        });
        store.timers.set(input.dedupeKey, {
          settle: null,
          // Armed here rather than at onSuccess: this is the backstop for every way an attempt can go
          // silent, including a mutation that never resolves at all.
          ttl: setTimeout(() => onTtl(input.dedupeKey), ATTEMPT_ENTRY_TTL_MS),
          grace: null,
        });
        syncProjectIds();
      },
      confirmSubmitted: (dedupeKey) => {
        const entry = store.entries.get(dedupeKey);
        // An abort or settle publish can land before the mutation's own onSuccess. Arming a timer for
        // an outcome already observed would fire a phantom timeout against a resolved attempt.
        if (entry === undefined || entry.phase !== "pending") {
          return;
        }
        writeEntry({ ...entry, acknowledged: true });
        const timers = store.timers.get(dedupeKey);
        if (timers === undefined || timers.settle !== null) {
          return;
        }
        timers.settle = setTimeout(() => onSettleTimeout(dedupeKey), ATTEMPT_SETTLE_TIMEOUT_MS);
      },
      discard: (dedupeKey) => removeEntry(dedupeKey),
      registerFeed: (taskId) => {
        store.feeds.set(taskId, (store.feeds.get(taskId) ?? 0) + 1);
        return () => {
          const next = (store.feeds.get(taskId) ?? 1) - 1;
          if (next > 0) {
            store.feeds.set(taskId, next);
          } else {
            store.feeds.delete(taskId);
          }
        };
      },
      ownsDedupeKey: (dedupeKey) => store.entries.has(dedupeKey),
      subscribe: (taskId, onStoreChange) => {
        const listeners = store.listeners.get(taskId) ?? new Set<() => void>();
        listeners.add(onStoreChange);
        store.listeners.set(taskId, listeners);
        return () => {
          listeners.delete(onStoreChange);
          if (listeners.size === 0) {
            store.listeners.delete(taskId);
          }
        };
      },
      getEntriesForTask: (taskId) => store.byTask.get(taskId) ?? EMPTY_ENTRIES,
    };
  }

  useEffect(() => {
    return () => {
      for (const [, timers] of store.timers) {
        for (const timer of [timers.settle, timers.ttl, timers.grace]) {
          if (timer !== null) {
            clearTimeout(timer);
          }
        }
      }
      store.timers.clear();
      // Entries go with their timers: refs survive React's simulated unmount, so an entry left behind
      // would render a card whose TTL and settle timers no longer exist.
      store.entries.clear();
      store.byTask.clear();
    };
  }, [store]);

  return (
    <AttemptTrackerContext.Provider value={contextRef.current}>
      {children}
      {/* Siblings, never wrappers: wrapping {children} would remount the page subtree on every
          live-project change. Duplicating a channel a page-level cache manager holds is safe — the
          duplicate-name throw is context-scoped and ACTIVE_CHANNEL_PROVIDER_COUNT detaches last. */}
      {projectIds.map((projectId) => (
        <AttemptChannelListener key={projectId} projectId={projectId} onMessage={onChannelMessage} />
      ))}
    </AttemptTrackerContext.Provider>
  );
};

interface AttemptChannelListenerProps {
  projectId: number;
  onMessage: (message: CacheMessageForType<typeof CacheMessageType.PROJECT_TASKS>) => void;
}

const AttemptChannelListener = ({ projectId, onMessage }: AttemptChannelListenerProps) => (
  <DetachableChannelProvider
    channelName={getCacheChannelName(CacheMessageType.PROJECT_TASKS, projectId)}
    options={ATTEMPT_CHANNEL_OPTIONS}
  >
    <AttemptChannelSubscriber projectId={projectId} onMessage={onMessage} />
  </DetachableChannelProvider>
);

const AttemptChannelSubscriber = ({ projectId, onMessage }: AttemptChannelListenerProps) => {
  useCacheChannel({ entityType: CacheMessageType.PROJECT_TASKS, entityId: projectId }, onMessage);
  return null;
};
